/* ==========================================================================
   lro-chrome.js
   Behaviour for the page chrome: header search, footer accordions and the
   back-to-top control.

   WHAT IS NOT IN HERE, ON PURPOSE
   The announcement ticker has no JavaScript at all. It rotates on a CSS
   keyframe and its pause control is a real checkbox, so it works, and can be
   stopped, with scripting disabled. Nothing below touches it.

   PROGRESSIVE ENHANCEMENT CONTRACT
     - The search box is a plain GET form to /search. With this file missing or
       blocked, typing and pressing Enter still runs a full search.
     - The footer columns are <details> elements shipped open. With this file
       missing they stay open and every link is reachable and crawlable.
     - The back-to-top control in the footer is an anchor to the main landmark.
       The floating button is created here and only on pointer-width screens,
       where it cannot collide with a sticky add-to-basket bar.

   No framework, no polyfill, no dependency. Vanilla, defer-loaded, idempotent:
   two sections both request this file, and browsers execute a duplicated
   <script src> twice, so the whole thing is wrapped in a one-shot guard.
   ========================================================================== */

(function () {
  'use strict';

  if (window.LRO_CHROME_READY) return;
  window.LRO_CHROME_READY = true;

  /* ------------------------------------------------------------------------
     Shared helpers
     ------------------------------------------------------------------------ */

  var REDUCED_MOTION = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  /* Every localStorage read and write is wrapped. Safari private mode and a
     blocked-storage policy both throw, and an unhandled throw here would take
     the rest of the chrome down with it. */
  function storeGet(key) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function storeSet(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  }

  function storeRemove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      /* nothing to do: the value was never readable in the first place */
    }
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* ========================================================================
     1. HEADER SEARCH
     ======================================================================== */

  var RECENT_KEY = 'lro:recent-searches';
  var RECENT_MAX = 5;

  function readRecent() {
    var list = storeGet(RECENT_KEY);
    if (!Array.isArray(list)) return [];
    return list
      .filter(function (item) {
        return typeof item === 'string' && item.trim().length > 0;
      })
      .slice(0, RECENT_MAX);
  }

  function pushRecent(term) {
    var value = String(term || '').trim();
    if (value.length < 2) return;
    var list = readRecent().filter(function (item) {
      return item.toLowerCase() !== value.toLowerCase();
    });
    list.unshift(value);
    storeSet(RECENT_KEY, list.slice(0, RECENT_MAX));
  }

  function dropRecent(term) {
    var value = String(term || '').toLowerCase();
    storeSet(
      RECENT_KEY,
      readRecent().filter(function (item) {
        return item.toLowerCase() !== value;
      })
    );
  }

  function LroSearch(root) {
    this.root = root;
    this.form = root.querySelector('[data-lro-search-form]');
    this.input = root.querySelector('[data-lro-search-input]');
    this.panel = root.querySelector('[data-lro-search-panel]');
    this.results = root.querySelector('[data-lro-search-results]');
    this.zero = root.querySelector('[data-lro-search-zero]');
    this.recentBlock = root.querySelector('[data-lro-recent]');
    this.recentList = root.querySelector('[data-lro-recent-list]');
    this.recentClear = root.querySelector('[data-lro-recent-clear]');
    this.scope = root.querySelector('[data-lro-search-scope]');
    this.clearButton = root.querySelector('[data-lro-search-clear]');
    this.status = root.querySelector('[data-lro-search-status]');
    this.scrim = root.querySelector('[data-lro-search-scrim]');

    if (!this.form || !this.input || !this.panel || !this.results) return;

    this.suggestUrl = root.getAttribute('data-suggest-url') || '/search/suggest';
    this.sectionId = root.getAttribute('data-section-id') || 'lro-predictive-search';
    this.limit = parseInt(root.getAttribute('data-limit'), 10) || 6;
    this.minChars = parseInt(root.getAttribute('data-min-chars'), 10) || 2;

    this.controller = null;
    this.timer = null;
    this.open = false;
    this.activeIndex = -1;
    this.options = [];
    this.lastQuery = '';
    this.blockedUntil = 0;

    this.bind();
    this.renderRecent();
    this.ready = true;
  }

  LroSearch.prototype.bind = function () {
    var self = this;

    /* The combobox contract is only announced once the script is running. With
       scripting off the input is an ordinary search field, which is exactly
       what it behaves like, so claiming a combobox in the markup would be a
       lie to a screen reader. */
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-haspopup', 'listbox');
    this.input.setAttribute('autocomplete', 'off');
    if (this.panel.id) this.input.setAttribute('aria-controls', this.panel.id);

    this.input.addEventListener('input', function () {
      self.root.setAttribute('data-has-query', self.input.value.length > 0 ? 'true' : 'false');
      self.schedule();
    });

    this.input.addEventListener('focus', function () {
      self.show();
      if (self.input.value.trim().length < self.minChars) self.showZero();
    });

    this.input.addEventListener('keydown', function (event) {
      self.onKeydown(event);
    });

    this.form.addEventListener('submit', function () {
      pushRecent(self.input.value);
      self.applyScope();
    });

    if (this.clearButton) {
      this.clearButton.addEventListener('click', function () {
        self.input.value = '';
        self.root.setAttribute('data-has-query', 'false');
        self.showZero();
        self.input.focus();
      });
    }

    if (this.scope) {
      this.scope.addEventListener('change', function () {
        if (self.input.value.trim().length >= self.minChars) self.schedule(0);
      });
    }

    if (this.recentClear) {
      this.recentClear.addEventListener('click', function () {
        storeRemove(RECENT_KEY);
        self.renderRecent();
        self.announce('Recent searches cleared');
      });
    }

    if (this.scrim) {
      this.scrim.addEventListener('click', function () {
        self.hide();
      });
    }

    /* Clicks and taps outside the component close it. Pointerdown rather than
       click, so a tap that starts outside does not first fire a click on a
       result row that is about to move. */
    document.addEventListener('pointerdown', function (event) {
      if (!self.open) return;
      if (self.root.contains(event.target)) return;
      self.hide();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && self.open) {
        self.hide();
        self.input.focus();
      }
    });

    /* Delegated, because the rows are replaced on every keystroke. */
    this.panel.addEventListener('click', function (event) {
      var remove = event.target.closest('[data-lro-recent-remove]');
      if (remove) {
        event.preventDefault();
        dropRecent(remove.getAttribute('data-term'));
        self.renderRecent();
        return;
      }

      var option = event.target.closest('[data-lro-option]');
      if (option) {
        var term = option.getAttribute('data-term');
        if (term) pushRecent(term);
      }
    });

    this.panel.addEventListener('mousemove', function (event) {
      var option = event.target.closest('[data-lro-option]');
      if (!option) return;
      var index = self.options.indexOf(option);
      if (index > -1 && index !== self.activeIndex) self.setActive(index, false);
    });
  };

  /* The scope selector folds into the query as an extra search term rather
     than a filter parameter, because Shopify's storefront search treats
     multiple terms as AND and indexes product_type. Choosing "Display" and
     typing "iphone 13" searches for iphone 13 within Display parts.
     With scripting off the selector is ignored and the search simply runs
     across the whole catalogue, which is a wider result, never a broken one. */
  LroSearch.prototype.scopedQuery = function () {
    var query = this.input.value.trim();
    if (!this.scope) return query;
    var term = this.scope.value;
    if (!term || !query) return query;
    if (query.toLowerCase().indexOf(term.toLowerCase()) > -1) return query;
    return query + ' ' + term;
  };

  LroSearch.prototype.applyScope = function () {
    if (!this.scope || !this.scope.value) return;
    var scoped = this.scopedQuery();
    if (scoped !== this.input.value) this.input.value = scoped;
  };

  LroSearch.prototype.schedule = function (delay) {
    var self = this;
    window.clearTimeout(this.timer);
    var wait = typeof delay === 'number' ? delay : 200;
    this.timer = window.setTimeout(function () {
      self.run();
    }, wait);
  };

  LroSearch.prototype.run = function () {
    var query = this.input.value.trim();

    if (query.length < this.minChars) {
      this.showZero();
      return;
    }

    if (Date.now() < this.blockedUntil) return;

    var scoped = this.scopedQuery();
    if (scoped === this.lastQuery && this.results.innerHTML.trim() !== '') {
      this.showResults();
      return;
    }
    this.lastQuery = scoped;

    this.fetch(scoped);
  };

  LroSearch.prototype.fetch = function (query) {
    var self = this;

    if (this.controller) this.controller.abort();
    this.controller = typeof AbortController === 'function' ? new AbortController() : null;

    var params = [
      'q=' + encodeURIComponent(query),
      'section_id=' + encodeURIComponent(this.sectionId),
      'resources[type]=product,collection,page,query',
      'resources[limit]=' + this.limit,
      'resources[limit_scope]=each',
      'resources[options][unavailable_products]=last',
      'resources[options][fields]=title,product_type,variants.sku,vendor,tag'
    ].join('&');

    this.root.setAttribute('data-busy', 'true');

    window
      .fetch(this.suggestUrl + '?' + params, {
        signal: this.controller ? this.controller.signal : undefined,
        headers: { Accept: 'text/html' }
      })
      .then(function (response) {
        if (response.status === 429) {
          /* Back off for as long as the server asks, and stay quiet about it.
             Hammering a rate limiter is how a search box gets a store
             throttled for everyone. */
          var retry = parseInt(response.headers.get('Retry-After'), 10);
          self.blockedUntil = Date.now() + (retry > 0 ? retry * 1000 : 10000);
          return null;
        }
        if (!response.ok) return null;
        return response.text();
      })
      .then(function (html) {
        self.root.setAttribute('data-busy', 'false');
        if (html === null || html === undefined) return;
        self.paint(html);
      })
      .catch(function (error) {
        self.root.setAttribute('data-busy', 'false');
        if (error && error.name === 'AbortError') return;
        /* A failed suggestion request is never fatal: the form still submits
           and the full search page still works. Fail silently rather than
           putting an error where results should be. */
      });
  };

  LroSearch.prototype.paint = function (html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fragment = parsed.querySelector('[data-lro-psearch]');

    this.results.innerHTML = fragment ? fragment.innerHTML : '';
    this.showResults();
    this.collectOptions();

    var count = fragment ? parseInt(fragment.getAttribute('data-result-count'), 10) : 0;
    if (!count) {
      this.announce('No parts match that search');
    } else if (count === 1) {
      this.announce('1 suggestion');
    } else {
      this.announce(count + ' suggestions');
    }
  };

  LroSearch.prototype.collectOptions = function () {
    var nodes = this.panel.querySelectorAll('[data-lro-option]');
    this.options = Array.prototype.slice.call(nodes);
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');

    for (var i = 0; i < this.options.length; i++) {
      if (!this.options[i].id) {
        this.options[i].id = (this.panel.id || 'lro-psearch') + '-option-' + i;
      }
      this.options[i].setAttribute('data-active', 'false');
    }
  };

  LroSearch.prototype.setActive = function (index, scroll) {
    if (!this.options.length) return;

    if (this.activeIndex > -1 && this.options[this.activeIndex]) {
      this.options[this.activeIndex].setAttribute('data-active', 'false');
    }

    /* Wrap in both directions. A list that stops dead at the ends makes people
       think the keyboard is broken. */
    if (index < 0) index = this.options.length - 1;
    if (index >= this.options.length) index = 0;

    this.activeIndex = index;
    var option = this.options[index];
    option.setAttribute('data-active', 'true');
    this.input.setAttribute('aria-activedescendant', option.id);

    if (scroll !== false && option.scrollIntoView) {
      option.scrollIntoView({ block: 'nearest' });
    }
  };

  LroSearch.prototype.onKeydown = function (event) {
    var key = event.key;

    if (key === 'Escape') {
      event.preventDefault();
      this.hide();
      return;
    }

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      if (!this.open) this.show();
      if (!this.options.length) this.collectOptions();
      if (!this.options.length) return;
      event.preventDefault();
      this.setActive(key === 'ArrowDown' ? this.activeIndex + 1 : this.activeIndex - 1);
      return;
    }

    if (key === 'Home' && this.options.length && this.activeIndex > -1) {
      event.preventDefault();
      this.setActive(0);
      return;
    }

    if (key === 'End' && this.options.length && this.activeIndex > -1) {
      event.preventDefault();
      this.setActive(this.options.length - 1);
      return;
    }

    if (key === 'Enter') {
      if (this.activeIndex > -1 && this.options[this.activeIndex]) {
        event.preventDefault();
        var option = this.options[this.activeIndex];
        var term = option.getAttribute('data-term');
        if (term) pushRecent(term);
        if (option.tagName === 'A') {
          window.location.href = option.getAttribute('href');
        } else {
          option.click();
        }
      } else {
        pushRecent(this.input.value);
        this.applyScope();
      }
    }
  };

  LroSearch.prototype.show = function () {
    if (this.open) return;
    this.open = true;
    this.panel.hidden = false;
    this.root.setAttribute('data-open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
  };

  LroSearch.prototype.hide = function () {
    if (!this.open) return;
    this.open = false;
    this.panel.hidden = true;
    this.root.setAttribute('data-open', 'false');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  };

  LroSearch.prototype.showZero = function () {
    this.results.innerHTML = '';
    this.lastQuery = '';
    if (this.zero) this.zero.hidden = false;
    this.renderRecent();
    this.show();
    this.collectOptions();
  };

  LroSearch.prototype.showResults = function () {
    if (this.zero) this.zero.hidden = true;
    this.show();
  };

  LroSearch.prototype.announce = function (message) {
    if (this.status) this.status.textContent = message;
  };

  LroSearch.prototype.renderRecent = function () {
    if (!this.recentBlock || !this.recentList) return;

    var terms = readRecent();
    if (!terms.length) {
      this.recentBlock.hidden = true;
      this.recentList.innerHTML = '';
      return;
    }

    var html = '';
    for (var i = 0; i < terms.length; i++) {
      var safe = terms[i]
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      html +=
        '<li class="lro-psearch__chip-wrap">' +
        '<a class="lro-psearch__chip" data-lro-option data-term="' +
        safe +
        '" href="' +
        this.searchHref(terms[i]) +
        '">' +
        safe +
        '<span class="lro-psearch__chip-remove" role="button" tabindex="0" ' +
        'data-lro-recent-remove data-term="' +
        safe +
        '" aria-label="Remove ' +
        safe +
        ' from recent searches">' +
        '<svg viewBox="0 0 10 10" aria-hidden="true" focusable="false">' +
        '<path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/>' +
        '</svg></span></a></li>';
    }

    this.recentList.innerHTML = html;
    this.recentBlock.hidden = false;
    this.collectOptions();
  };

  LroSearch.prototype.searchHref = function (term) {
    var action = this.form.getAttribute('action') || '/search';
    return action + '?q=' + encodeURIComponent(term) + '&type=product';
  };

  /* ========================================================================
     2. FOOTER COLUMNS
     Shipped open so the no-script footer is fully expanded. Closed here on
     narrow viewports only, and the contact column is left open because it is
     the reason most people scroll this far.
     ======================================================================== */

  function initFooterColumns(root) {
    var columns = root.querySelectorAll('[data-lro-foot-col]');
    if (!columns.length || !window.matchMedia) return;

    var narrow = window.matchMedia('(max-width: 47.99em)');

    function sync() {
      for (var i = 0; i < columns.length; i++) {
        var keepOpen = columns[i].hasAttribute('data-lro-foot-open');
        columns[i].open = !narrow.matches || keepOpen;
      }
    }

    sync();

    if (typeof narrow.addEventListener === 'function') {
      narrow.addEventListener('change', sync);
    } else if (typeof narrow.addListener === 'function') {
      narrow.addListener(sync);
    }
  }

  /* ========================================================================
     3. BACK TO TOP
     The footer already carries an anchor to the main landmark that works with
     scripting off. This adds the floating button, on pointer-width screens
     only, so it cannot land on top of a sticky add-to-basket bar.
     ======================================================================== */

  function initBackToTop() {
    if (document.querySelector('.lro-totop')) return;
    if (!window.matchMedia || !window.matchMedia('(min-width: 48em)').matches) return;

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'lro-totop lro-scope';
    button.setAttribute('aria-label', 'Back to the top of the page');
    button.setAttribute('data-visible', 'false');
    button.innerHTML =
      '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
      '<path d="M8 13V3M3 8l5-5 5 5" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    document.body.appendChild(button);

    button.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });

      /* Move focus as well as the viewport. Scrolling alone leaves a keyboard
         user tabbing on from the footer, which is not "back to top" at all. */
      var main = document.getElementById('MainContent');
      if (main) {
        if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
      }
    });

    var ticking = false;

    function update() {
      ticking = false;
      button.setAttribute('data-visible', window.scrollY > 800 ? 'true' : 'false');
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* ========================================================================
     Boot
     ======================================================================== */

  function boot() {
    var searches = document.querySelectorAll('[data-lro-search]');
    for (var i = 0; i < searches.length; i++) {
      if (searches[i].getAttribute('data-lro-ready') === 'true') continue;
      searches[i].setAttribute('data-lro-ready', 'true');
      /* eslint-disable-next-line no-new */
      new LroSearch(searches[i]);
    }

    var footers = document.querySelectorAll('[data-lro-foot]');
    for (var j = 0; j < footers.length; j++) {
      if (footers[j].getAttribute('data-lro-ready') === 'true') continue;
      footers[j].setAttribute('data-lro-ready', 'true');
      initFooterColumns(footers[j]);
      if (footers[j].getAttribute('data-lro-totop') === 'true') initBackToTop();
    }
  }

  onReady(boot);

  /* The theme editor tears sections down and rebuilds them. Re-boot on the
     Shopify event rather than leaving a freshly rendered footer inert. */
  document.addEventListener('shopify:section:load', boot);
})();
